import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import type { Poll, PollStatus, PollingStrategyConfig } from './types';
import { DEFAULT_POLLING_STRATEGY } from './types';

const STORAGE_KEY = 'socrative_polls';

export type PollAction =
  | { type: 'add'; poll: Omit<Poll, 'pollCount'> }
  | { type: 'remove'; pollId: string }
  | { type: 'updateLastPolled'; pollId: string }
  | {
      type: 'updateStatus';
      pollId: string;
      status: PollStatus;
      error?: string;
    }
  | { type: 'updateData'; pollId: string; data: unknown }
  | { type: 'incrementPollCount'; pollId: string };

function reducer(state: Poll[], action: PollAction): Poll[] {
  switch (action.type) {
    case 'add': {
      const exists = state.some((poll) => poll.id === action.poll.id);
      if (exists) return state;

      return [
        ...state,
        {
          ...action.poll,
          pollCount: 0,
        },
      ];
    }
    case 'remove':
      return state.filter((poll) => poll.id !== action.pollId);
    case 'updateLastPolled':
      return state.map((poll) =>
        poll.id === action.pollId ? { ...poll, lastPolledAt: Date.now() } : poll
      );
    case 'updateStatus':
      return state.map((poll) =>
        poll.id === action.pollId
          ? { ...poll, status: action.status, error: action.error }
          : poll
      );
    case 'updateData':
      return state.map((poll) =>
        poll.id === action.pollId ? { ...poll, data: action.data } : poll
      );
    case 'incrementPollCount':
      return state.map((poll) =>
        poll.id === action.pollId ? { ...poll, pollCount: poll.pollCount + 1 } : poll
      );
  }
}

const basePollsAtom = atomWithStorage<Poll[]>(STORAGE_KEY, []);

export const pollsAtom = atom(
  (get) => {
    const polls = get(basePollsAtom) || [];
    const now = Date.now();

    const validPolls = polls.filter((poll) => {
      const maxDuration = DEFAULT_POLLING_STRATEGY.maxDuration;
      return now - poll.startedAt < maxDuration;
    });

    if (validPolls.length !== polls.length) {
      setTimeout(() => {
        const currentPolls = get(basePollsAtom) || [];
        const stillValid = currentPolls.filter((poll) => {
          const maxDuration = DEFAULT_POLLING_STRATEGY.maxDuration;
          return Date.now() - poll.startedAt < maxDuration;
        });
        if (stillValid.length !== currentPolls.length) {
          // Cleanup handled by setter
        }
      }, 0);
    }

    return validPolls;
  },
  (get, set, action: PollAction) => {
    const current = get(basePollsAtom);
    set(basePollsAtom, reducer(current, action));
  }
);

export function getPollingInterval(
  poll: Poll,
  strategy: PollingStrategyConfig = DEFAULT_POLLING_STRATEGY,
  now: number = Date.now()
): number {
  const elapsed = now - poll.startedAt;
  const maxDuration = strategy.maxDuration ?? 5 * 60 * 1000;
  const timeRemaining = maxDuration - elapsed;

  // If maxDuration exceeded, return 0
  if (timeRemaining <= 0) {
    return 0;
  }

  // Handle initial delay (before first poll)
  const initialDelayMs = strategy.initialDelayMs ?? 0;
  if (poll.pollCount === 0 && elapsed < initialDelayMs) {
    const delay = initialDelayMs - elapsed;
    return Math.min(delay, timeRemaining);
  }

  // Handle fast polling phase
  const fastPollingInterval = strategy.fastPollingInterval;
  const fastPollingDuration = strategy.fastPollingDuration;
  const hasFastPolling =
    fastPollingInterval != null && fastPollingDuration != null;

  if (hasFastPolling) {
    const elapsedSincePollingBegan = elapsed - initialDelayMs;
    if (elapsedSincePollingBegan < fastPollingDuration) {
      return Math.min(fastPollingInterval, timeRemaining);
    }
    // Fast polling phase has expired, transition to max interval
    if (!strategy.useExponentialBackoff) {
      return Math.min(strategy.maxInterval, timeRemaining);
    }
  }

  // Standard polling logic (no fast polling configured)
  if (!strategy.useExponentialBackoff) {
    return Math.min(strategy.initialInterval, timeRemaining);
  }

  // Exponential backoff
  const multiplier = poll.pollCount;
  const interval = Math.min(
    strategy.initialInterval * Math.pow(2, multiplier),
    strategy.maxInterval
  );

  return Math.min(interval, timeRemaining);
}

export function hasExceededMaxDuration(
  poll: Poll,
  maxDuration = DEFAULT_POLLING_STRATEGY.maxDuration
): boolean {
  return Date.now() - poll.startedAt >= maxDuration;
}
